import {
  type ReactNode,
  type ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { IconUser } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import styles from './AvatarUpload.module.css';

const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/webp';
const AVATAR_ICON_SIZE = 32;

interface AvatarUploadProps {
  onFileSelected: (file: File) => void;
}

export const AvatarUpload = memo(function AvatarUpload({
  onFileSelected,
}: AvatarUploadProps): ReactNode {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
      const objectUrl = URL.createObjectURL(file);
      previewUrlRef.current = objectUrl;
      setPreviewUrl(objectUrl);
      onFileSelected(file);
    },
    [onFileSelected]
  );

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  return (
    <div className={styles.wrapper}>
      <button
        type="button"
        className={styles.circle}
        onClick={handleClick}
        aria-label={t('invitation.profile.avatar')}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={t('invitation.profile.avatar')}
            className={styles.preview}
          />
        ) : (
          <IconUser size={AVATAR_ICON_SIZE} />
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        className={styles.hiddenInput}
        onChange={handleChange}
      />
      <span className={styles.label} onClick={handleClick} role="presentation">
        {t('invitation.profile.avatar')}
      </span>
    </div>
  );
});
